import React, { useState, useEffect } from "react";
import Page from "components/Page";
import { withTranslation } from "react-i18next";
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Card from "components/Card";
import { encodeId, getStorage, setStorage } from "utils";
import constants from "../../../constants";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import useModuleDisplayOwner, {
  getModuleDisplayOwnerName,
} from "hooks/useModuleDisplayOwner";
import "./OfficePolicyManagement.scss";
import "../Scheduler/Scheduler.scss";
import styles from "./OfficePolicyManagement.module.scss";
import { MAIN_TAB, COMPLIANCE_SUB_TAB, STAFF_SUB_TAB } from "./tabs/constants";
import ComplianceDashboard from "./tabs/ComplianceDashboard";
import StaffTracking from "./tabs/StaffTracking";
import PolicyHeaderActions from "./components/PolicyHeaderActions";
import AssignedToYouView from "./components/AssignedToYouView";
import { PolicySelectionProvider } from "./context/PolicySelectionContext";
import {
  StaffTrackingSelectionProvider,
  useStaffTrackingSelection,
} from "./context/StaffTrackingSelectionContext";

const cacheKeys = constants.officePolicies?.cache || {};
const ASSIGNMENT_VIEW = {
  assignedByYou: "assignedByYou",
  assignedToYou: "assignedToYou",
};

/** Clears Staff Tracking row selection when navigating away from that tab. */
function StaffTrackingSelectionSync({ mainTab }) {
  const { clearStaffSelection } = useStaffTrackingSelection();
  useEffect(() => {
    if (mainTab !== MAIN_TAB.staffTracking) {
      clearStaffSelection();
    }
  }, [mainTab, clearStaffSelection]);
  return null;
}

const OfficePolicyManagement = ({ t, history }) => {
  const { isOwner, isStaff, dataAccessValues } = useAccountOwnerCommon();
  const displayOwner = useModuleDisplayOwner();
  const canShowAddPolicy = isOwner || (isStaff && dataAccessValues?.isAdmin);
  const isOwnerOrAdmin = isOwner || (isStaff && dataAccessValues?.isAdmin);
  const isStaffOnly = isStaff && !dataAccessValues?.isAdmin;

  const [mainTab, setMainTab] = useState(() => {
    try {
      const cached = getStorage(cacheKeys.officePolicyMainTab);
      return cached?.mainTab || MAIN_TAB.complianceDashboard;
    } catch {
      return MAIN_TAB.complianceDashboard;
    }
  });
  const [complianceSubTab, setComplianceSubTab] = useState(() => {
    try {
      const cached = getStorage(cacheKeys.officePolicyComplianceSub);
      return cached?.subTab ?? COMPLIANCE_SUB_TAB.currentPolicies;
    } catch {
      return COMPLIANCE_SUB_TAB.currentPolicies;
    }
  });
  const [staffSubTab, setStaffSubTab] = useState(() => {
    try {
      const cached = getStorage(cacheKeys.officePolicyStaffSub);
      return cached?.subTab ?? STAFF_SUB_TAB.currentPolicies;
    } catch {
      return STAFF_SUB_TAB.currentPolicies;
    }
  });
  const [assignmentView, setAssignmentView] = useState(() => {
    if (!cacheKeys.officePolicyAssignmentView)
      return ASSIGNMENT_VIEW.assignedByYou;
    try {
      const cached = getStorage(cacheKeys.officePolicyAssignmentView);
      return cached?.view === ASSIGNMENT_VIEW.assignedToYou
        ? ASSIGNMENT_VIEW.assignedToYou
        : ASSIGNMENT_VIEW.assignedByYou;
    } catch {
      return ASSIGNMENT_VIEW.assignedByYou;
    }
  });
  const [staffTrackingPayload, setStaffTrackingPayload] = useState({});
  const [assignmentDropdownOpen, setAssignmentDropdownOpen] = useState(false);
  useEffect(() => {
    if (cacheKeys.officePolicyMainTab) {
      setStorage(cacheKeys.officePolicyMainTab, { mainTab });
    }
  }, [mainTab]);
  useEffect(() => {
    if (cacheKeys.officePolicyComplianceSub) {
      setStorage(cacheKeys.officePolicyComplianceSub, {
        subTab: complianceSubTab,
      });
    }
  }, [complianceSubTab]);
  useEffect(() => {
    if (cacheKeys.officePolicyStaffSub) {
      setStorage(cacheKeys.officePolicyStaffSub, { subTab: staffSubTab });
    }
  }, [staffSubTab]);
  useEffect(() => {
    if (cacheKeys.officePolicyAssignmentView && isOwnerOrAdmin) {
      setStorage(cacheKeys.officePolicyAssignmentView, {
        view: assignmentView,
      });
    }
  }, [assignmentView, isOwnerOrAdmin]);

  const isFromOwnerOfficeModule = getStorage("isFromOwnerOfficeModule");
  const isChatFromDashBoard = getStorage("isChatFromDashBoard");

  const goToPreviousScreen = () => {
    if (isFromOwnerOfficeModule?.officeSlection) {
      history.push({
        pathname: constants.routes.accountOwner.officeOptions.replace(
          ":officeId",
          encodeId(isFromOwnerOfficeModule.officeId),
        ),
      });
      return;
    }
    if (isFromOwnerOfficeModule?.isAdminControl) {
      history.push({
        pathname: constants.routes.staff.officeAdmin.replace(
          ":officeId",
          encodeId(isFromOwnerOfficeModule.officeId),
        ),
      });
      return;
    }
    if (isFromOwnerOfficeModule?.staffOfficeSlection) {
      history.push({
        pathname: constants.routes.staff.officeOptions.replace(
          ":officeId",
          encodeId(isFromOwnerOfficeModule.officeId),
        ),
      });
      return;
    }
    history.push(
      isChatFromDashBoard
        ? constants.routes.dashboard.managerDashboard
        : "/Offices",
    );
  };

  const activeMainTab = mainTab || MAIN_TAB.complianceDashboard;
  const staffTrackingTabActive = activeMainTab === MAIN_TAB.staffTracking;
  const showAssignedToYouContent =
    isStaffOnly ||
    (isOwnerOrAdmin && assignmentView === ASSIGNMENT_VIEW.assignedToYou);

  const renderAssignmentDropdown = () =>
    isOwnerOrAdmin ? (
      <Dropdown
        isOpen={assignmentDropdownOpen}
        toggle={() => setAssignmentDropdownOpen((prev) => !prev)}
        className={styles["assignment_view_dropdown"]}
      >
        <DropdownToggle
          tag="div"
          className={styles["assignment_dropdown_toggle"]}
          caret
        >
          {assignmentView === ASSIGNMENT_VIEW.assignedByYou
            ? t("officePolicyManagement.assignedByYou")
            : t("officePolicyManagement.assignedToYou")}
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem
            onClick={() => {
              setAssignmentView(ASSIGNMENT_VIEW.assignedByYou);
            }}
          >
            {t("officePolicyManagement.assignedByYou")}
          </DropdownItem>
          <DropdownItem
            onClick={() => {
              setAssignmentView(ASSIGNMENT_VIEW.assignedToYou);
            }}
          >
            {t("officePolicyManagement.assignedToYou")}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    ) : null;

  const renderMainTabs = () => (
    <Nav tabs>
      <NavItem>
        <NavLink
          className={
            activeMainTab === MAIN_TAB.complianceDashboard ? "active" : ""
          }
          onClick={() => setMainTab(MAIN_TAB.complianceDashboard)}
        >
          {t("officePolicyManagement.complianceDashboard")}
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          className={activeMainTab === MAIN_TAB.staffTracking ? "active" : ""}
          onClick={() => setMainTab(MAIN_TAB.staffTracking)}
        >
          {t("officePolicyManagement.staffTracking")}
        </NavLink>
      </NavItem>
    </Nav>
  );

  const renderTabsContent = () => {
    return (
      <TabContent activeTab={activeMainTab}>
        <TabPane tabId={MAIN_TAB.complianceDashboard}>
          {activeMainTab === MAIN_TAB.complianceDashboard && (
            <ComplianceDashboard
              activeSubTab={complianceSubTab}
              setActiveSubTab={setComplianceSubTab}
            />
          )}
        </TabPane>
        <TabPane tabId={MAIN_TAB.staffTracking}>
          {activeMainTab === MAIN_TAB.staffTracking && (
            <StaffTracking
              activeSubTab={staffSubTab}
              setActiveSubTab={setStaffSubTab}
              setStaffTrackingPayload={setStaffTrackingPayload}
            />
          )}
        </TabPane>
      </TabContent>
    );
  };

  const pageHeaderActions = (() => {
    if (showAssignedToYouContent && !isOwnerOrAdmin) return null;
    return (
      <PolicyHeaderActions
        t={t}
        canShowAddPolicy={canShowAddPolicy}
        complianceSubTab={
          showAssignedToYouContent
            ? COMPLIANCE_SUB_TAB.currentPolicies
            : complianceSubTab
        }
        history={history}
        className={styles["policy-hearder-box"]}
        staffTrackingTabActive={
          showAssignedToYouContent ? false : staffTrackingTabActive
        }
        staffTrackingPayload={
          showAssignedToYouContent ? {} : staffTrackingPayload
        }
      />
    );
  })();

  return (
    <PolicySelectionProvider>
      <StaffTrackingSelectionProvider>
        <StaffTrackingSelectionSync mainTab={activeMainTab} />
      <Page
        onBack={goToPreviousScreen}
        className="scheduler-page"
        titleKey="officePolicyManagement.policiesAndProcedures"
        actionButton={pageHeaderActions}
      >
        {isOwnerOrAdmin && (
          <div
            className={` ${styles["assignment_dropdown_row"]}  ${styles["assignee-switcher"]} `}
          >
            {renderAssignmentDropdown()}
          </div>
        )}
        {isStaff &&
          displayOwner?.id &&
          !showAssignedToYouContent && (
            <div className="show-owner-name-text">
              {t("officePolicyManagement.ownerAdminHeadingForOfficePolicy")}
              <span>
                {` '${getModuleDisplayOwnerName(displayOwner)}' `}
              </span>
            </div>
          )}
        <Card className={styles["policy_tabs_card"]}>
          {showAssignedToYouContent ? (
            <AssignedToYouView t={t} />
          ) : (
            <div className="common-tabs scheduler-tabs">
              {renderMainTabs()}
              {renderTabsContent()}
            </div>
          )}
        </Card>
      </Page>
      </StaffTrackingSelectionProvider>
    </PolicySelectionProvider>
  );
};

export default withTranslation()(OfficePolicyManagement);
